import React, { useEffect, useRef, useState } from "react";
import { Printer, FileArrowDown, FileText, CheckCircle, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { resolveMedia } from "@/lib/api";
import { cacheImageDataUrl, loadImageDataUrl } from "@/lib/offlineStore";
import { downloadBlankScoreSheet, downloadCurrentGamePdf, printScoreSheet } from "@/lib/emergencyScoreSheet";

// Fetch a remote image and store it as a base64 data URL so the PDF can embed it offline.
async function precacheLogo(logoUrl) {
  if (!logoUrl || loadImageDataUrl(logoUrl)) return;
  try {
    const res = await fetch(resolveMedia(logoUrl), { mode: "cors" });
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
    cacheImageDataUrl(logoUrl, dataUrl);
  } catch { /* offline or blocked — PDF falls back to text-only team name */ }
}

// sk = the useScorekeeper hook return value
export function EmergencyPdfMenu({ sk }) {
  const { game, teamOf, rosterOf, adds, home, away, timeouts, teamFoulsOf, playerById } = sk;
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const ref = useRef(null);

  const homeTeam = game ? teamOf(game.home_team_id) : null;
  const awayTeam = game ? teamOf(game.away_team_id) : null;
  const homeRoster = game ? rosterOf(game.home_team_id) : [];
  const awayRoster = game ? rosterOf(game.away_team_id) : [];

  // Pre-cache team logos (online) so the emergency PDF has them even offline,
  // and mark the sheet "ready" once we have game + rosters available locally.
  useEffect(() => {
    if (!game) return;
    precacheLogo(homeTeam?.logo_url);
    precacheLogo(awayTeam?.logo_url);
    setReady(!!(homeTeam && awayTeam));
  }, [game, homeTeam, awayTeam]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!game) return null;

  const pdfData = { game, homeTeam, awayTeam, homeRoster, awayRoster };

  const buildCurrent = () => {
    const byPlayer = {};
    Object.entries(adds || {}).forEach(([pid, a]) => {
      byPlayer[pid] = { pts: a.pts || 0, pf: a.pf || 0 };
    });
    // Ensure any scored player not in the roster list still appears keyed correctly.
    return {
      homeScore: home,
      awayScore: away,
      byPlayer,
      teamFouls: { home: teamFoulsOf("home"), away: teamFoulsOf("away") },
      timeouts: { home: timeouts?.home || 0, away: timeouts?.away || 0 },
    };
  };

  const doBlank = () => {
    try { downloadBlankScoreSheet(pdfData); toast.success("Blank score sheet downloaded"); }
    catch (e) { toast.error("Could not generate PDF"); }
    setOpen(false);
  };
  const doCurrent = () => {
    try { downloadCurrentGamePdf(pdfData, buildCurrent()); toast.success("Current game PDF downloaded"); }
    catch (e) { toast.error("Could not generate PDF"); }
    setOpen(false);
  };
  const doPrint = () => {
    try { printScoreSheet(pdfData, buildCurrent()); }
    catch (e) { toast.error("Could not open print view"); }
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid="emergency-pdf">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="emergency-pdf-btn"
        title="Emergency paper score sheet (works offline)"
        className="flex items-center gap-2 border border-[#FFB300] text-[#FFB300] hover:bg-[#FFB300]/10 font-bold uppercase tracking-wide px-5 py-3.5 transition-all duration-200"
      >
        <Printer size={20} weight="fill" /> Emergency PDF <CaretDown size={14} />
      </button>

      {ready && (
        <span data-testid="emergency-pdf-ready" title="Score sheet can be generated offline" className="hidden sm:flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#00E676] mt-1 absolute -bottom-4 left-0">
          <CheckCircle size={12} weight="fill" /> Ready offline
        </span>
      )}

      {open && (
        <div data-testid="emergency-pdf-menu" className="absolute z-30 mt-2 left-0 w-64 border border-white/15 bg-[#151515] shadow-2xl">
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-[10px] uppercase tracking-wide text-[#FFB300] font-bold">Paper Backup</p>
            <p className="text-white/40 text-[11px] leading-snug mt-0.5">Works with no internet. Teams &amp; rosters are already filled in.</p>
          </div>
          <button onClick={doBlank} data-testid="emergency-download-blank" className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-white/85 hover:bg-white/5 transition-colors">
            <FileText size={18} className="text-[#FFB300] shrink-0" />
            <span><span className="font-bold">Download Blank</span><br /><span className="text-white/40 text-[11px]">Empty printable sheet</span></span>
          </button>
          <button onClick={doCurrent} data-testid="emergency-download-current" className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-white/85 hover:bg-white/5 transition-colors border-t border-white/5">
            <FileArrowDown size={18} className="text-[#FF5722] shrink-0" />
            <span><span className="font-bold">Download Current Game</span><br /><span className="text-white/40 text-[11px]">Current score &amp; stats</span></span>
          </button>
          <button onClick={doPrint} data-testid="emergency-print" className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-white/85 hover:bg-white/5 transition-colors border-t border-white/5">
            <Printer size={18} className="text-white/70 shrink-0" />
            <span><span className="font-bold">Print Score Sheet</span><br /><span className="text-white/40 text-[11px]">Open browser print dialog</span></span>
          </button>
        </div>
      )}
    </div>
  );
}
